import { Pagination, type PaginationProps } from 'antd';
import type { ReactNode } from 'react';

import type { Pagination as PaginationParams } from '@/utils/types';

import { DEFAULT_PAGE_SIZE_OPTIONS } from './constants';
import { getShowTotal } from './utils';

type Props = {
  pagination: PaginationProps;
  paginationParams?: PaginationParams;
  rightSide?: ReactNode;
  setPagination: (page: number, pageSize: number) => void;
  /** Overrides the page-size choices offered by the size changer. */
  pageSizeOptions?: number[];
};

const TablePagination = ({
  pagination: parentPagination,
  paginationParams,
  rightSide,
  setPagination,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS
}: Props) => {
  const current = paginationParams?.page ?? 1;
  const pageSize = paginationParams?.page_size ?? 10;

  const handleChange: PaginationProps['onChange'] = (page, newPageSize) => {
    // If the parent supplied an onChange handler, prefer delegating to it so
    // callers that expect to receive the raw pagination event continue to
    // work (tests call the stored pagination.onChange directly). Otherwise
    // use the table's internal setPagination.
    if (typeof parentPagination.onChange === 'function') {
      return parentPagination.onChange(page, newPageSize);
    }

    const isPageSizeChange = newPageSize !== pageSize;

    if (isPageSizeChange) {
      setPagination(1, newPageSize);
    } else {
      setPagination(page, newPageSize);
    }
  };

  const handleShowSizeChange: PaginationProps['onShowSizeChange'] = (
    _current,
    size
  ) => {
    if (typeof parentPagination.onShowSizeChange === 'function') {
      return parentPagination.onShowSizeChange(_current, size);
    }

    setPagination(1, size);
  };

  return (
    <Pagination
      {...parentPagination}
      align={rightSide ? 'start' : 'end'}
      current={current}
      pageSize={pageSize}
      showTotal={getShowTotal(parentPagination)}
      pageSizeOptions={pageSizeOptions}
      onChange={handleChange}
      onShowSizeChange={handleShowSizeChange}
      showSizeChanger
    />
  );
};

export default TablePagination;
